import {
  Avatar,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme
} from '@mui/material';
import useQueryUrl from 'src/hooks/useQuery';
import { useNavigate } from 'react-router';
import Skeleton from '@mui/material/Skeleton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useQuery } from '@apollo/client';
import { GET_WHATSAPP_ALERT_DETAIL } from 'src/services/graphql/alertSystem/query';
import ReactMarkdown from 'react-markdown';
import { useAlert } from 'src/hooks/useAlert';
import { ContentCopy, Message } from '@mui/icons-material';
import { useMemo } from 'react';
import { markdownToWhatsapp } from 'src/utils/markdownToWhatsapp';

export const OnLoading = () => {
  return (
    <Stack spacing={1} width={'100%'} p={'2%'}>
      {/* For other variants, adjust the size with `width` and `height` */}

      <Stack direction="row" justifyContent="space-between" sx={{ mb: 10 }}>
        <Skeleton variant="circular" width={100} height={100} />
        <Skeleton variant="rectangular" width={'20%'} height={60} />
      </Stack>
      <Stack direction="row" justifyContent="center">
        <Skeleton variant="rectangular" width={'50%'} height={50} />
      </Stack>

      <Skeleton variant="rectangular" width={'100%'} height={300} />

      <Box sx={{ mt: 10, px: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ margin: 1 }}>
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Skeleton width="100%">
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
        <Skeleton variant="rectangular" width="100%">
          <div style={{ paddingTop: '57%' }} />
        </Skeleton>
      </Box>
      <Box sx={{ mt: 10, px: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ margin: 1 }}>
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Skeleton width="100%">
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
        <Skeleton variant="rectangular" width="100%">
          <div style={{ paddingTop: '57%' }} />
        </Skeleton>
      </Box>
      <Box sx={{ mt: 10, px: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ margin: 1 }}>
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Skeleton width="100%">
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
        <Skeleton variant="rectangular" width="100%">
          <div style={{ paddingTop: '57%' }} />
        </Skeleton>
      </Box>
      <Box sx={{ mt: 10, px: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ margin: 1 }}>
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          </Box>
          <Box sx={{ width: '100%' }}>
            <Skeleton width="100%">
              <Typography>.</Typography>
            </Skeleton>
          </Box>
        </Box>
        <Skeleton variant="rectangular" width="100%">
          <div style={{ paddingTop: '57%' }} />
        </Skeleton>
      </Box>
    </Stack>
  );
};

// Component untuk artikel tidak ditemukan
const MessageNotFound = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Card
        elevation={3}
        sx={{
          borderRadius: 3,
          textAlign: 'center',
          padding: 4,
          maxWidth: { xs: '90%', lg: '40%' },
          width: '100%'
        }}
      >
        <CardContent>
          {/* Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <Message sx={{ height: 50, width: 50, color: 'gray' }} />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 2
            }}
          >
            Message Not Found
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, lineHeight: 1.6 }}
          >
            Sorry, the message you're looking for isn't available at this time.
            It may have been deleted or unpublished.
          </Typography>

          {/* Action Button */}
          <Button
            variant="contained"
            startIcon={<ArrowBackIosNewIcon />}
            onClick={handleGoBack}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              boxShadow: 2
            }}
          >
            Back to Dashboard
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export const AlertPreview = () => {
  const query = useQueryUrl();
  const id = query.get('id');
  const category = query.get('category');

  const theme = useTheme();

  const { handleClickAlert } = useAlert();

  const { data, loading } = useQuery<{
    alertSystem_getWhatsappAlertDetailById: string;
  }>(GET_WHATSAPP_ALERT_DETAIL, {
    variables: {
      id,
      category
    }
  });

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
    handleClickAlert({
      message: 'Text has Copied',
      severity: 'success',
      horizontal: 'center',
      vertical: 'top'
    });
  };

  const formattedMessage = useMemo(() => {
    if (!data) return '';
    return data.alertSystem_getWhatsappAlertDetailById.replace(
      /(?<!\*)\*([^*\n]+)\*(?!\*)/g,
      '**$1**'
    );
  }, [data]);

  return (
    <>
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: theme.colors.primary.light
        }}
      >
        {loading ? (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              maxWidth: { xs: '100%', md: '50%' },
              backgroundColor: '#FFFFFFFF',
              overflowY: 'scroll',
              margin: 0
            }}
          >
            <OnLoading />
          </Box>
        ) : !data ? (
          <MessageNotFound />
        ) : (
          <Box
            sx={{
              height: '90%',
              width: '100%',
              maxWidth: { xs: '100%', md: '50%' },
              backgroundColor: 'white',
              overflowY: 'scroll',
              margin: 0,
              borderRadius: '16px'
            }}
          >
            <Stack
              sx={{
                backgroundColor: 'white',
                padding: '24px',
                borderBottom: `1px solid ${theme.palette.grey[300]}`
              }}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Stack spacing={1}>
                <Typography variant="h3">NoLimit Dashboard</Typography>
                <Typography variant="h6">Automated Whatsapp Alert</Typography>
              </Stack>
              <Button
                onClick={() =>
                  handleCopyText(
                    markdownToWhatsapp(
                      data.alertSystem_getWhatsappAlertDetailById
                    )
                  )
                }
                variant="outlined"
              >
                <Stack
                  alignItems="center"
                  spacing={1}
                  direction="row"
                  sx={{ padding: '8px' }}
                >
                  <ContentCopy sx={{ fontSize: '16px', color: '#4B4B4B' }} />
                  <Typography fontSize="12px">Copy Content</Typography>
                </Stack>
              </Button>
            </Stack>
            <Stack
              sx={{
                padding: '24px'
              }}
            >
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1
                      style={{
                        lineHeight: '36px'
                      }}
                    >
                      {children}
                    </h1>
                  ),
                  ul: ({ children }) => (
                    <ul
                      style={{
                        listStyleType: 'disc',
                        paddingLeft: '24px',
                        margin: '8px 0'
                      }}
                    >
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol
                      style={{
                        listStyleType: 'decimal',
                        paddingLeft: '24px',
                        margin: '8px 0'
                      }}
                    >
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li style={{ display: 'list-item', marginBottom: '4px' }}>
                      {children}
                    </li>
                  ),
                  img: ({ src, alt }) => (
                    <img
                      src={src}
                      alt={alt || ''}
                      style={{
                        maxWidth: '300px',
                        height: 'auto'
                      }}
                    />
                  )
                }}
              >
                {formattedMessage?.replace(/\n/g, '  \n')}
              </ReactMarkdown>
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AlertPreview;
